import { Suspense } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { ErrorBoundary } from "react-error-boundary"
import { Sidebar } from "@edemics/components/sidebar"
import { Header } from "@edemics/components/header"
import { AppShellChromeProvider } from "@edemics/components/app-shell-chrome-context"
import { ChatRenderer } from "@edemics/components/chat-renderer"
import { AuthGuard } from "./AuthGuard"
import { ErrorFallback } from "./ErrorFallback"
import { RouteLoadingFallback } from "./LoadingFallback"

/**
 * Tenant-app shell. Every authenticated route lands inside this.
 *
 * Chrome: <Sidebar> + <Header> + scrollable <main> — provided once
 * here for every route so pages don't have to render their own. The
 * AppShellChromeProvider around <Outlet /> makes any page-level
 * <Sidebar> or <Header> render null (they detect the context flag
 * and short-circuit), preventing double-render for pages that were
 * ported with their own layout.
 *
 * The shell also owns:
 *   - AuthGuard          — redirect to /login when unauthenticated
 *   - ErrorBoundary      — page-level errors don't blank the shell;
 *                          resetKeys={[pathname]} recovers on nav
 *   - Suspense           — lazy chunk loading shows the skeleton
 */
export function AppShell() {
  const location = useLocation()

  return (
    <AuthGuard>
      <div className="flex h-screen overflow-hidden bg-light-bg dark:bg-dark-bg">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden min-w-0">
          <Header />
          <main className="flex-1 overflow-y-auto min-h-0 bg-[#F4F5FA] dark:bg-[#28243D]">
            <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[location.pathname]}>
              <Suspense fallback={<RouteLoadingFallback />}>
                <AppShellChromeProvider>
                  <Outlet />
                </AppShellChromeProvider>
              </Suspense>
            </ErrorBoundary>
          </main>
        </div>
      </div>
      {/*
        ChatRenderer — ported from EdemicsAllModule/components/
        chat-renderer.tsx (2026-09-12). Floating minimized-chat icon
        anchored bottom-right, expands into the draggable ChatPopup.
        Uses the ChatProvider from packages/contexts/src/chat-context
        which is already mounted in Providers. Placed outside the
        flex container so its fixed positioning isn't clipped by the
        sidebar/main overflow-hidden.
      */}
      <ChatRenderer />
    </AuthGuard>
  )
}
